import React, { useState, useEffect, useRef } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { Link } from 'react-scroll';
import { Helmet } from 'react-helmet';
import { 
  FaSearch, 
  FaInstagram, 
  FaFacebookF, 
  FaTwitter, 
  FaLinkedinIn, 
  FaCamera, 
  FaCog, 
  FaArrowRight, 
  FaChevronDown,
  FaCheckCircle,
  FaLaptopCode,
  FaChartLine,
  FaRocket,
  FaLayerGroup,
  FaShieldAlt,
  FaMobileAlt,
  FaRegFileAlt,
  FaHashtag,
  FaLink,
  FaMapMarkerAlt,
  FaComments,
  FaRegLightbulb,
  FaUserFriends,
  FaBullhorn,
  FaPencilAlt,
  FaRegChartBar,
  FaSlidersH,
  FaCloudUploadAlt,
  FaSync
} from 'react-icons/fa';

// Throttle function for performance optimization
const throttle = (func, limit) => {
  let inThrottle;
  return function() {
    const args = arguments;
    const context = this;
    if (!inThrottle) {
      func.apply(context, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  }
};

const DigitalMarketingServices = () => {
  const [activeSection, setActiveSection] = useState('hero');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { scrollYProgress } = useScroll();
  
  // Parallax effect for hero section
  const heroOpacity = useTransform(scrollYProgress, [0, 0.1], [1, 0]);
  const heroY = useTransform(scrollYProgress, [0, 0.1], [0, -50]);
  
  // Refs for each section for scroll detection
  const sectionRefs = {
    hero: useRef(null),
    seo: useRef(null),
    social: useRef(null),
    photography: useRef(null),
    maintenance: useRef(null),
    why: useRef(null),
  };

  // Structured Data Schema
  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": "https://techecosolution.in/digital-marketing-services",
        "url": "https://techecosolution.in/digital-marketing-services",
        "name": "Digital Marketing Services - SEO, Social Media & More | Techecosolution",
        "description": "Professional digital marketing services including SEO optimization, social media marketing, product photography, and website maintenance. Boost your online presence and drive more sales.",
        "inLanguage": "en-US",
        "isPartOf": {
          "@type": "WebSite",
          "@id": "https://techecosolution.in",
          "url": "https://techecosolution.in",
          "name": "Techecosolution",
          "publisher": {
            "@type": "Organization",
            "@id": "https://techecosolution.in#organization"
          }
        },
        "primaryImageOfPage": {
          "@type": "ImageObject",
          "url": "https://techecosolution.in/images/digital-marketing-banner.jpg",
          "width": 1200,
          "height": 630
        },
        "breadcrumb": {
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://www.techecosolution.in"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Services",
              "item": "https://www.techecosolution.in/digital-marketing-services"
            },
            {
              "@type": "ListItem",
              "position": 3,
              "name": "Digital Marketing",
              "item": "https://www.techecosolution.in/digital-marketing-services"
            }
          ]
        }
      },
      {
        "@type": "Organization",
        "@id": "https://www.techecosolution.in#organization",
        "name": "Techecosolution",
        "url": "https://www.techecosolution.in",
        "logo": {
          "@type": "ImageObject",
          "url": "https://www.techecosolution.in/logo.png",
          "width": 300,
          "height": 100
        },
        "contactPoint": {
          "@type": "ContactPoint",
          "telephone": "+91-9483085077",
          "contactType": "customer service",
          "areaServed": "IN",
          "availableLanguage": "English"
        },
        "sameAs": [
          "https://linkedin.com/company/techecosolution",
          "https://twitter.com/techecosolution",
          "https://facebook.com/techecosolution",
          "https://instagram.com/techecosolution"
        ],
        "address": {
          "@type": "PostalAddress",
          "addressCountry": "IN",
          "addressRegion": "Your State",
          "addressLocality": "Your City"
        }
      },
      {
        "@type": "Service",
        "serviceType": "Digital Marketing Services",
        "provider": {
          "@type": "Organization",
          "@id": "https://www.techecosolution.in.in#organization"
        },
        "hasOfferCatalog": {
          "@type": "OfferCatalog",
          "name": "Digital Marketing Services",
          "itemListElement": [
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "SEO Optimization",
                "description": "Comprehensive SEO services to improve search engine rankings and drive organic traffic."
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Social Media Marketing",
                "description": "Strategic social media marketing across all major platforms to build brand awareness and engagement."
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Product Photography",
                "description": "Professional product photography to showcase your products and increase conversions."
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Website Maintenance",
                "description": "Comprehensive website maintenance services to keep your site secure and optimized."
              }
            }
          ]
        }
      },
      {
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "What digital marketing services do you offer?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "We offer comprehensive digital marketing services including SEO optimization, social media marketing, product photography, website maintenance, content creation, and paid advertising campaigns across all major platforms."
            }
          },
          {
            "@type": "Question",
            "name": "How does SEO optimization help my business?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "SEO optimization improves your website's visibility in search engines, leading to increased organic traffic, better brand awareness, and more qualified leads. Our SEO strategies include keyword research, on-page optimization, technical SEO, and link building."
            }
          },
          {
            "@type": "Question",
            "name": "Which social media platforms do you manage?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "We manage all major social media platforms including Facebook, Instagram, Twitter, LinkedIn, and YouTube. We create platform-specific content strategies and run targeted advertising campaigns to maximize your reach and engagement."
            }
          },
          {
            "@type": "Question",
            "name": "What is included in your website maintenance service?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Our website maintenance includes regular security updates, performance optimization, content updates, backup and recovery, mobile responsiveness testing, and ongoing SEO monitoring to ensure your website runs smoothly and securely."
            }
          },
          {
            "@type": "Question",
            "name": "How do you measure digital marketing success?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "We measure success through comprehensive analytics including website traffic growth, search engine rankings, social media engagement rates, conversion rates, lead generation, and ROI. We provide detailed monthly reports with actionable insights."
            }
          }
        ]
      }
    ]
  };
  
  // Track which section is currently in view
  useEffect(() => {
    const handleScroll = throttle(() => {
      const scrollPosition = window.scrollY + 100;
      
      for (const section in sectionRefs) {
        const element = sectionRefs[section].current;
        if (element) {
          const offsetTop = element.offsetTop;
          const height = element.offsetHeight;
          
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + height) {
            setActiveSection(section);
            break;
          }
        }
      }
    }, 16);
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  // Custom cursor with glow effect
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [cursorVariant, setCursorVariant] = useState("default");
  
  useEffect(() => {
    const mouseMove = throttle((e) => {
      setMousePosition({
        x: e.clientX,
        y: e.clientY
      });
    }, 16);
    
    window.addEventListener("mousemove", mouseMove);
    
    return () => {
      window.removeEventListener("mousemove", mouseMove);
    };
  }, []);

  // Core Web Vitals tracking
  useEffect(() => {
    if (typeof window !== 'undefined') {
      import('web-vitals').then(({ getCLS, getFID, getFCP, getLCP, getTTFB }) => {
        getCLS(console.log);
        getFID(console.log);
        getFCP(console.log);
        getLCP(console.log);
        getTTFB(console.log);
      }).catch(() => {
        // Handle import error silently
      });
    }
  }, []);
  
  const variants = {
    default: {
      x: mousePosition.x - 16,
      y: mousePosition.y - 16,
      opacity: 0.5
    },
    hover: {
      x: mousePosition.x - 24,
      y: mousePosition.y - 24,
      height: 48,
      width: 48,
      opacity: 0.8,
      backgroundColor: "rgba(192, 183, 232, 0.5)",
      mixBlendMode: "screen"
    }
  };
  
  const textEnter = () => setCursorVariant("hover");
  const textLeave = () => setCursorVariant("default");

  const seoServices = [
    {
      title: "Keyword Research & Strategy",
      description: "We analyze high-search volume, low-competition keywords to ensure your business appears in relevant searches.",
      icon: <FaHashtag className="text-purple-400 text-4xl mb-4" />,
      image: "https://images.unsplash.com/photo-1518186285589-2f7649de83e0?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1974&q=80"
    },
    {
      title: "On-Page SEO Optimization",
      description: "We optimize your website's content, URLs, meta tags, headings, and internal linking for better rankings and user experience.",
      icon: <FaRegFileAlt className="text-purple-400 text-4xl mb-4" />,
      image: "https://images.unsplash.com/photo-1467232004584-a241de8bcf5d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2069&q=80"
    },
    {
      title: "Technical SEO Improvements",
      description: "Improve your website's loading speed, mobile usability, and crawlability, ensuring Google indexation and better rankings.",
      icon: <FaLaptopCode className="text-purple-400 text-4xl mb-4" />,
      image: "https://images.unsplash.com/photo-1461749280684-dccba630e2f6?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2069&q=80"
    },
    {
      title: "Link Building Strategy",
      description: "Strengthen your website's authority with high-quality backlinks from trusted sources, improving credibility and rankings.",
      icon: <FaLink className="text-purple-400 text-4xl mb-4" />,
      image: "https://images.unsplash.com/photo-1497493292307-31c376b6e479?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2071&q=80"
    },
    {
      title: "Local SEO Domination",
      description: "We optimize Google My Business (GMB), location-based keywords, and citations to help you rank higher in local searches.",
      icon: <FaMapMarkerAlt className="text-purple-400 text-4xl mb-4" />,
      image: "https://images.unsplash.com/photo-1569212889298-82b52f38702e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1974&q=80"
    },
    {
      title: "SEO Analytics & Reporting",
      description: "Get detailed SEO performance reports, including traffic insights, keyword rankings, and conversion tracking.",
      icon: <FaRegChartBar className="text-purple-400 text-4xl mb-4" />,
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80"
    }
  ];

  const socialPlatforms = [
    {
      platform: "Facebook",
      icon: <FaFacebookF className="text-white text-2xl" />,
      color: "bg-blue-600",
      image: "https://images.unsplash.com/photo-1611162618071-b39a2ec055fb?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1974&q=80"
    },
    {
      platform: "Instagram",
      icon: <FaInstagram className="text-white text-2xl" />,
      color: "bg-gradient-to-br from-purple-600 via-pink-600 to-orange-500",
      image: "https://images.unsplash.com/photo-1611162616305-c69b3fa7fbe0?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1974&q=80"
    },
    {
      platform: "Twitter",
      icon: <FaTwitter className="text-white text-2xl" />,
      color: "bg-blue-400",
      image: "https://images.unsplash.com/photo-1611605698335-8b1569810432?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2074&q=80"
    },
    {
      platform: "LinkedIn",
      icon: <FaLinkedinIn className="text-white text-2xl" />,
      color: "bg-blue-700",
      image: "https://images.unsplash.com/photo-1611944212129-29977ae1398c?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2074&q=80"
    }
  ];

  const socialServices = [
    {
      title: "Targeted Social Media Campaigns",
      description: "We design high-converting social media strategies that attract, engage, and convert your ideal customers across all major platforms.",
      icon: <FaBullhorn className="text-purple-400 text-4xl" />,
      image: "https://images.unsplash.com/photo-1568992688065-536aad8a12f6?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2072&q=80",
      position: "right"
    },
    {
      title: "Content Creation & Strategy",
      description: "Develop engaging social media content that drives engagement, including viral posts, videos, and interactive stories tailored to your audience.",
      icon: <FaPencilAlt className="text-purple-400 text-4xl" />,
      image: "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80",
      position: "left"
    },
    {
      title: "Community Engagement & Growth",
      description: "We respond to comments, messages, and reviews, fostering a strong connection with your audience and building a loyal community.",
      icon: <FaComments className="text-purple-400 text-4xl" />,
      image: "https://images.unsplash.com/photo-1556155092-490a1ba16284?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80",
      position: "right"
    },
    {
      title: "Paid Social Advertising",
      description: "Run targeted ad campaigns on Facebook, Instagram, LinkedIn, and more to generate leads and sales with optimized ROI.",
      icon: <FaRegChartBar className="text-purple-400 text-4xl" />,
      image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2015&q=80",
      position: "left"
    }
  ];

  const photographyImages = [
    "https://images.unsplash.com/photo-1523275335684-37898b6baf30?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1999&q=80",
    "https://images.unsplash.com/photo-1491553895911-0055eca6402d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1780&q=80",
    "https://images.unsplash.com/photo-1572635196237-14b3f281503f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2080&q=80",
    "https://images.unsplash.com/photo-1503602642458-232111445657?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1974&q=80",
    "https://images.unsplash.com/photo-1560343090-f0409e92791a?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1964&q=80",
    "https://images.unsplash.com/photo-1583394838336-acd977736f90?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1968&q=80"
  ];

  const photographyServices = [
    {
      title: "Professional Studio Photography",
      description: "Showcase your products with crisp, high-definition images in a controlled studio environment.",
      icon: <FaCamera className="text-purple-400 text-3xl mb-4" />
    },
    {
      title: "Lifestyle Product Photography",
      description: "Show your products in real-world settings to help customers visualize using them.",
      icon: <FaCamera className="text-purple-400 text-3xl mb-4" />
    },
    {
      title: "360° Product Views",
      description: "Give customers a complete view of your products from every angle to increase confidence.",
      icon: <FaSync className="text-purple-400 text-3xl mb-4" />
    },
    {
      title: "E-Commerce Optimization",
      description: "Images optimized for Amazon, Shopify, and other platforms to meet their specific requirements.",
      icon: <FaCloudUploadAlt className="text-purple-400 text-3xl mb-4" />
    }
  ];

  const maintenanceServices = [
    {
      title: "Regular Updates & Security Patches",
      description: "Keep your website secure with the latest software, plugins, and security updates.",
      icon: <FaShieldAlt className="text-purple-400 text-4xl mb-4" />,
      image: "https://images.unsplash.com/photo-1563013544-824ae1b704d3?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80"
    },
    {
      title: "Performance Optimization",
      description: "Speed up your website with advanced optimization techniques for better user experience and SEO.",
      icon: <FaRocket className="text-purple-400 text-4xl mb-4" />,
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80"
    },
    {
      title: "Backup & Disaster Recovery",
      description: "Regular backups and restore points ensure your data is safe and recoverable in case of emergencies.",
      icon: <FaCloudUploadAlt className="text-purple-400 text-4xl mb-4" />,
      image: "https://images.unsplash.com/photo-1614064641938-3bbee52942c7?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80"
    },
    {
      title: "Mobile Responsiveness",
      description: "Ensure your website works perfectly on all devices with responsive design testing and optimization.",
      icon: <FaMobileAlt className="text-purple-400 text-4xl mb-4" />,
      image: "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80"
    },
    {
      title: "Content Updates & Management",
      description: "Regular content updates to keep your website fresh, engaging, and relevant to your audience.",
      icon: <FaRegFileAlt className="text-purple-400 text-4xl mb-4" />,
      image: "https://images.unsplash.com/photo-1499750310107-5fef28a66643?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80"
    },
    {
      title: "SEO Monitoring & Optimization",
      description: "Ongoing SEO improvements to maintain and improve your search engine rankings.",
      icon: <FaChartLine className="text-purple-400 text-4xl mb-4" />,
      image: "https://images.unsplash.com/photo-1572177812156-58036aae439c?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80"
    }
  ];

  const whyChooseUs = [
    {
      title: "Proven SEO & Social Media Strategies",
      description: "Get higher search rankings, more leads, and better engagement with our data-driven approach.",
      icon: <FaChartLine className="text-purple-400 text-4xl" />,
      image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2015&q=80"
    },
    {
      title: "Tailored Digital Marketing Campaigns",
      description: "We create custom strategies based on your business goals and target audience.",
      icon: <FaRegLightbulb className="text-purple-400 text-4xl" />,
      image: "https://images.unsplash.com/photo-1533750349088-cd871a92f312?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80"
    },
    {
      title: "Expert Team of Marketers & Designers",
      description: "Our specialists deliver data-driven, high-performing campaigns that get results.",
      icon: <FaUserFriends className="text-purple-400 text-4xl" />,
      image: "https://images.unsplash.com/photo-1600880292089-90a7e086ee0c?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1974&q=80"
    },
    {
      title: "End-to-End Website Maintenance",
      description: "Keep your site secure, optimized, and running at peak performance with our comprehensive support.",
      icon: <FaLaptopCode className="text-purple-400 text-4xl" />,
      image: "https://images.unsplash.com/photo-1550751827-4bd374c3f58b?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80"
    },
    {
      title: "High-Quality Product Photography",
      description: "Showcase your products with professional photography that increases conversions and sales.",
      icon: <FaCamera className="text-purple-400 text-4xl" />,
      image: "https://images.unsplash.com/photo-1542038784456-1ea8e935640e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80"
    },
    {
      title: "Transparent Reporting & Analytics",
      description: "Track your growth with detailed performance insights and regular reporting.",
      icon: <FaRegChartBar className="text-purple-400 text-4xl" />,
      image: "https://images.unsplash.com/photo-1590859808308-3d2d9c515b1a?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2074&q=80"
    }
  ];
  
  return (
    <>
      <Helmet>
        {/* Primary Meta Tags */}
        <title>Digital Marketing Services - SEO, Social Media & More | Techecosolution</title>
        <meta name="description" content="Professional digital marketing services including SEO optimization, social media marketing, product photography, and website maintenance. Boost your online presence and drive more sales." />
        <meta name="keywords" content="digital marketing services, SEO optimization, social media marketing, product photography, website maintenance, online marketing, digital marketing agency India" />
        
        {/* Geographic Targeting */}
        <meta name="geo.region" content="IN" />
        <meta name="geo.placename" content="India" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=5.0" />
        
        {/* Open Graph Meta Tags */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Digital Marketing Services - SEO, Social Media & More | Techecosolution" />
        <meta property="og:description" content="Professional digital marketing services including SEO optimization, social media marketing, product photography, and website maintenance. Boost your online presence and drive more sales." />
        <meta property="og:image" content="https://www.techecosolution.in/images/digital-marketing-banner.jpg" />
        <meta property="og:url" content="https://www.techecosolution.in/digital-marketing-services" />
        <meta property="og:site_name" content="Techecosolution" />

        {/* Twitter Card Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Digital Marketing Services - SEO, Social Media & More" />
        <meta name="twitter:description" content="Professional digital marketing services including SEO optimization, social media marketing, product photography, and website maintenance." />
        <meta name="twitter:image" content="https://www.techecosolution.in/images/digital-marketing-banner.jpg" />

        {/* Canonical URL */}
        <link rel="canonical" href="https://www.techecosolution.in/digital-marketing-services" />

        {/* Preconnect for Performance */}
        <link rel="preconnect" href="https://images.unsplash.com" />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        
        {/* Language */}
        <html lang="en" />
      </Helmet>

      {/* Structured Data */}
      <script 
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />

      <main role="main" className="bg-black text-white min-h-screen overflow-x-hidden relative">
        {/* Custom cursor */}
        <motion.div
          className="custom-cursor fixed top-0 left-0 w-8 h-8 rounded-full bg-purple-400 z-50 pointer-events-none mix-blend-exclusion"
          variants={variants}
          animate={cursorVariant}
          transition={{ type: "spring", stiffness: 500, damping: 28 }}
          aria-hidden="true"
        />
        
        {/* Navigation */}
        <nav className="fixed top-0 left-0 w-full z-40 bg-black bg-opacity-80 backdrop-blur-lg border-b border-purple-900/30" role="navigation" aria-label="Main Navigation">
          <div className="container mx-auto px-4 py-4 flex justify-between items-center">
            <motion.div 
              className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-blue-500"
              whileHover={{ scale: 1.05 }}
              onMouseEnter={textEnter}
              onMouseLeave={textLeave}
            >
              <Link to="hero" spy={true} smooth={true} duration={500} className="cursor-pointer">
                Techecosolution
              </Link>
            </motion.div>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex space-x-6">
              {[
                { id: 'hero', label: 'Home' },
                { id: 'seo', label: 'SEO' },
                { id: 'social', label: 'Social Media' },
                { id: 'photography', label: 'Photography' },
                { id: 'maintenance', label: 'Maintenance' },
                { id: 'why', label: 'Why Us' }
              ].map((item) => (
                <Link
                  key={item.id}
                  to={item.id}
                  spy={true}
                  smooth={true}
                  duration={500}
                  className={`cursor-pointer transition-all duration-300 relative ${
                    activeSection === item.id 
                      ? 'text-purple-400' 
                      : 'text-gray-300 hover:text-white'
                  }`}
                  onMouseEnter={textEnter}
                  onMouseLeave={textLeave}
                  aria-current={activeSection === item.id ? 'page' : undefined}
                >
                  {item.label}
                  
                  {activeSection === item.id && (
                    <motion.div 
                      className="absolute -bottom-2 left-0 right-0 h-0.5 bg-gradient-to-r from-purple-400 to-blue-500"
                      layoutId="activeSection"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.3 }}
                    />
                  )}
                </Link>
              ))}
            </div>
            
            {/* Mobile Navigation Toggle */}
            <div className="md:hidden">
              <button 
                className="text-white p-2"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                onMouseEnter={textEnter}
                onMouseLeave={textLeave}
                aria-label="Toggle mobile menu"
                aria-expanded={isMenuOpen}
              >
                <div className="w-6 h-5 flex flex-col justify-between">
                  <span className={`w-full h-0.5 bg-white transform transition-all duration-300 ${isMenuOpen ? 'rotate-45 translate-y-2' : ''}`}></span>
                  <span className={`w-full h-0.5 bg-white transition-all duration-300 ${isMenuOpen ? 'opacity-0' : 'opacity-100'}`}></span>
                  <span className={`w-full h-0.5 bg-white transform transition-all duration-300 ${isMenuOpen ? '-rotate-45 -translate-y-2' : ''}`}></span>
                </div>
              </button>
            </div>
          </div>
          
          {/* Mobile Menu */}
          <AnimatePresence>
            {isMenuOpen && (
              <motion.div 
                className="md:hidden bg-black bg-opacity-95 backdrop-blur-lg"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                <div className="container mx-auto px-4 py-4 flex flex-col space-y-4">
                  {[
                    { id: 'hero', label: 'Home' },
                    { id: 'seo', label: 'SEO' },
                    { id: 'social', label: 'Social Media' },
                    { id: 'photography', label: 'Photography' },
                    { id: 'maintenance', label: 'Maintenance' },
                    { id: 'why', label: 'Why Us' }
                  ].map((item) => (
                    <Link
                      key={item.id}
                      to={item.id}
                      spy={true}
                      smooth={true}
                      duration={500}
                      className={`cursor-pointer py-2 px-4 rounded-lg transition-all duration-300 ${
                        activeSection === item.id 
                          ? 'bg-purple-900/30 text-purple-400' 
                          : 'text-gray-300 hover:bg-purple-900/10'
                      }`}
                      onClick={() => setIsMenuOpen(false)}
                      onMouseEnter={textEnter}
                      onMouseLeave={textLeave}
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </nav>
        
        {/* Hero Section */}
        <motion.header 
          ref={sectionRefs.hero}
          id="hero" 
          className="min-h-screen flex items-center justify-center relative overflow-hidden pt-20"
          style={{ opacity: heroOpacity, y: heroY }}
        >
          {/* Background elements */}
          <div className="absolute inset-0 z-0" aria-hidden="true">
            <div className="absolute w-96 h-96 rounded-full bg-purple-900/20 top-1/4 -left-48 blur-3xl animate-pulse-slow"></div>
            <div className="absolute w-96 h-96 rounded-full bg-blue-900/20 bottom-1/4 -right-48 blur-3xl animate-float"></div>
            <div className="absolute w-64 h-64 rounded-full bg-indigo-900/10 bottom-1/3 left-1/3 blur-3xl animate-float-slow"></div>
            
            {/* Grid pattern */}
            <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:50px_50px] opacity-20"></div>
            
            {/* Hero background image with overlay */}
            <div className="absolute inset-0 opacity-20">
              <img 
                src="https://images.unsplash.com/photo-1519389950473-47ba0277781c?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80" 
                alt="Digital marketing team working on campaigns and strategy"
                className="w-full h-full object-cover"
                loading="eager"
                decoding="async"
                width="2070"
                height="1380"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black via-black/80 to-black"></div>
            </div>
          </div>
          
          <div className="container mx-auto px-4 z-10">
            <div className="flex flex-col lg:flex-row items-center gap-12">
              <div className="lg:w-1/2">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  className="mb-6 inline-block"
                >
                  <span className="px-4 py-1 rounded-full text-sm font-medium bg-gradient-to-r from-purple-500/20 to-blue-500/20 border border-purple-500/30">
                    Digital Marketing Experts
                  </span>
                </motion.div>
                
                <motion.h1 
                  className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.1 }}
                  onMouseEnter={textEnter}
                  onMouseLeave={textLeave}
                >
                  <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-blue-500">
                    Digital Marketing Services
                  </span>
                  <br /> 
                  <span className="text-white">Elevate Your Brand Online</span>
                </motion.h1>
                
                <motion.p 
                  className="text-lg md:text-xl text-gray-300 mb-10"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                >
                  At Techecosolution, we help businesses grow online with comprehensive SEO optimization, strategic social media marketing, professional product photography, and reliable website maintenance. Our data-driven digital marketing strategies enhance brand visibility, drive targeted traffic, and significantly improve conversion rates.
                </motion.p>
                
                <motion.div
                  className="flex flex-col sm:flex-row gap-4"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                >
                  <Link
                    to="seo"
                    spy={true}
                    smooth={true}
                    duration={500}
                  >
                    <motion.button 
                      className="px-8 py-4 rounded-full text-base font-medium bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 shadow-lg shadow-purple-600/20 flex items-center justify-center gap-2 group"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.98 }}
                      onMouseEnter={textEnter}
                      onMouseLeave={textLeave}
                      aria-label="Explore our digital marketing services"
                    >
                      Explore Our Services
                      <FaArrowRight className="transition-transform group-hover:translate-x-1" aria-hidden="true" />
                    </motion.button>
                  </Link>
                  
                  <Link
                    to="why"
                    spy={true}
                    smooth={true}
                    duration={500}
                  >
                    <motion.button 
                      className="px-8 py-4 rounded-full text-base font-medium border border-purple-500/50 hover:bg-purple-900/20 transition-all flex items-center justify-center gap-2"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.98 }}
                      onMouseEnter={textEnter}
                      onMouseLeave={textLeave}
                      aria-label="Learn why you should choose Techecosolution"
                    >
                      Why Choose Us
                    </motion.button>
                  </Link>
                </motion.div>
              </div>
              
              <motion.div 
                className="lg:w-1/2"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <div className="relative">
                  <img 
                    src="https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80" 
                    alt="Professional digital marketing team collaborating on marketing strategies and campaigns"
                    className="rounded-2xl shadow-2xl shadow-purple-900/20 border border-purple-500/20"
                    loading="eager"
                    decoding="async"
                    width="2070"
                    height="1380"
                  />
                  <div className="absolute -bottom-6 -right-6 bg-gradient-to-br from-purple-600 to-blue-600 p-4 rounded-2xl shadow-lg transform rotate-3">
                    <div className="flex items-center gap-3">
                      <FaChartLine className="text-white text-xl" aria-hidden="true" />
                      <span className="text-white font-bold">500+ Clients</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
          
          {/* Scroll indicator */}
          <motion.div 
            className="absolute bottom-10 left-1/2 transform -translate-x-1/2"
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            aria-hidden="true"
          >
            <FaChevronDown className="text-purple-400 animate-pulse" />
          </motion.div>
        </motion.header>
        
        {/* SEO Section */}
        <section 
          ref={sectionRefs.seo}
          id="seo" 
          className="py-20 relative overflow-hidden"
          aria-labelledby="seo-heading"
        >
          {/* Background elements */}
          <div className="absolute inset-0 z-0" aria-hidden="true">
            <div className="absolute w-96 h-96 rounded-full bg-purple-900/10 top-1/3 -right-48 blur-3xl"></div>
            <div className="absolute w-64 h-64 rounded-full bg-blue-900/10 bottom-1/4 left-1/4 blur-3xl"></div>
            
            {/* Background image with overlay */}
            <div className="absolute inset-0 opacity-10">
              <img 
                src="https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2072&q=80" 
                alt="SEO analytics and optimization dashboard"
                className="w-full h-full object-cover"
                loading="lazy"
                decoding="async"
                width="2072"
                height="1381"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black via-black/90 to-black"></div>
            </div>
          </div>
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="flex flex-col lg:flex-row items-center gap-12 mb-16">
              <motion.div 
                className="lg:w-1/2"
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.5 }}
              >
                <h2 id="seo-heading" className="text-3xl md:text-5xl font-bold mb-6">
                  <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-blue-500">
                    SEO Optimization Services
                  </span>
                  <span className="block text-white mt-2">Rank Higher & Get More Traffic</span>
                </h2>
                <div className="w-24 h-1 bg-gradient-to-r from-purple-400 to-blue-500 mb-6" aria-hidden="true"></div>
                <p className="text-gray-300 text-lg mb-8">
                  Boost your website's Google rankings with proven SEO best practices, helping you attract qualified leads and high-intent buyers. Our comprehensive SEO optimization strategies are designed to increase your online visibility, improve search engine rankings, and drive sustainable organic traffic that converts into customers.
                </p>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                  {[
                    "Keyword Research & Strategy",
                    "On-Page SEO Optimization", 
                    "Link Building & Authority",
                    "Technical SEO & Speed"
                  ].map((feature, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <FaCheckCircle className="text-purple-400 text-xl mt-1 flex-shrink-0" aria-hidden="true" />
                      <span className="text-gray-300">{feature}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
              
              <motion.div 
                className="lg:w-1/2"
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.5 }}
              >
                <img 
                  src="https://images.unsplash.com/photo-1562577309-4932fdd64cd1?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2074&q=80" 
                  alt="SEO optimization dashboard showing search rankings and analytics data"
                  className="rounded-2xl shadow-2xl shadow-purple-900/20 border border-purple-500/20"
                  loading="lazy"
                  decoding="async"
                  width="2074"
                  height="1382"
                />
              </motion.div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {seoServices.map((item, index) => (
                <motion.article
                  key={index}
                  className="bg-gradient-to-br from-purple-900/20 to-black border border-purple-900/30 rounded-2xl overflow-hidden hover:border-purple-500/50 transition-all group relative"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  whileHover={{ y: -5 }}
                  onMouseEnter={textEnter}
                  onMouseLeave={textLeave}
                >
                  {/* Card image */}
                  <div className="h-48 overflow-hidden">
                    <img 
                      src={item.image} 
                      alt={`${item.title} - Professional SEO service illustration`}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      loading="lazy"
                      decoding="async"
                      width="400"
                      height="300"
                    />
                  </div>
                  
                  {/* Card content */}
                  <div className="p-6 relative">
                    {/* Glow effect on hover */}
                    <div className="absolute -inset-1 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg blur opacity-0 group-hover:opacity-20 transition-opacity duration-1000" aria-hidden="true"></div>
                    
                    <div className="relative">
                      <div className="flex items-center mb-4" aria-hidden="true">
                        {item.icon}
                      </div>
                      <h3 className="text-xl font-bold mb-3 text-white group-hover:text-purple-400 transition-colors">{item.title}</h3>
                      <p className="text-gray-300">{item.description}</p>
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
            
            <motion.div 
              className="mt-16 text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h3 className="text-2xl font-bold mb-6 text-white">Let's Boost Your Online Visibility!</h3>
              <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
                Increase organic traffic, brand awareness, and sales with our expert SEO optimization strategies that deliver measurable results.
              </p>
              <motion.button 
                className="px-8 py-4 rounded-full text-base font-medium bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 shadow-lg shadow-purple-600/20 flex items-center justify-center gap-2 mx-auto group"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                onMouseEnter={textEnter}
                onMouseLeave={textLeave}
                aria-label="Start your SEO optimization journey"
              >
                Get Started with SEO
                <FaArrowRight className="transition-transform group-hover:translate-x-1" aria-hidden="true" />
              </motion.button>
            </motion.div>
          </div>
        </section>
        
        {/* Social Media Section */}
        <section 
          ref={sectionRefs.social}
          id="social" 
          className="py-20 relative overflow-hidden"
          aria-labelledby="social-heading"
        >
          {/* Background elements */}
          <div className="absolute inset-0 z-0" aria-hidden="true">
            <div className="absolute w-96 h-96 rounded-full bg-blue-900/10 top-1/4 -left-48 blur-3xl"></div>
            <div className="absolute w-64 h-64 rounded-full bg-purple-900/10 bottom-1/3 right-1/3 blur-3xl"></div>
            
            {/* Background image with overlay */}
            <div className="absolute inset-0 opacity-10">
              <img 
                src="https://images.unsplash.com/photo-1611162616475-46b635cb6868?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2074&q=80" 
                alt="Social media marketing campaign on various platforms"
                className="w-full h-full object-cover"
                loading="lazy"
                decoding="async"
                width="2074"
                height="1383"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black via-black/90 to-black"></div>
            </div>
                          
            {/* Animated social media icons background */}
            <div className="absolute inset-0 overflow-hidden opacity-5">
              <div className="absolute top-1/4 left-1/4 animate-float-slow">
                <FaFacebookF className="text-6xl" />
              </div>
              <div className="absolute top-1/3 right-1/4 animate-float">
                <FaInstagram className="text-6xl" />
              </div>
              <div className="absolute bottom-1/4 left-1/3 animate-float-slow">
                <FaTwitter className="text-6xl" />
              </div>
              <div className="absolute bottom-1/3 right-1/3 animate-float">
                <FaLinkedinIn className="text-6xl" />
              </div>
            </div>
          </div>
                        
          <div className="container mx-auto px-4 relative z-10">
            <motion.header 
              className="text-center mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.5 }}
            >
              <h2 id="social-heading" className="text-3xl md:text-5xl font-bold mb-6">
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-blue-500">
                  Social Media Marketing
                </span>
                <span className="block text-white mt-2">Expand Your Brand's Reach</span>
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-purple-400 to-blue-500 mx-auto mb-6" aria-hidden="true"></div>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                Strategic social media marketing across all major platforms to build brand awareness, engage your audience, and drive conversions.
              </p>
            </motion.header>
                          
            {/* Social media platforms display */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
              {socialPlatforms.map((item, index) => (
                <motion.article
                  key={index}
                  className="rounded-2xl overflow-hidden shadow-lg shadow-purple-900/20 group relative"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  whileHover={{ y: -5 }}
                >
                  <div className="h-40 overflow-hidden">
                    <img 
                      src={item.image} 
                      alt={`${item.platform} social media marketing and management services`}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      loading="lazy"
                      decoding="async"
                      width="400"
                      height="300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent"></div>
                  </div>
                  <div className={`absolute bottom-0 left-0 right-0 p-4 flex items-center justify-between ${item.color} bg-opacity-80`}>
                    <span className="font-bold text-white">{item.platform}</span>
                    <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center" aria-hidden="true">
                      {item.icon}
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
                          
            {/* Zigzag layout for social media services */}
            <div className="space-y-16">
              {socialServices.map((item, index) => (
                <motion.div
                  key={index}
                  className={`flex flex-col ${item.position === 'left' ? 'md:flex-row' : 'md:flex-row-reverse'} items-center gap-8`}
                  initial={{ opacity: 0, x: item.position === 'left' ? -50 : 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.5 }}
                >
                  <div className="w-full md:w-1/2">
                    <motion.article 
                      className="bg-gradient-to-br from-purple-900/20 to-black border border-purple-900/30 rounded-2xl p-8 hover:border-purple-500/50 transition-all group relative overflow-hidden"
                      whileHover={{ y: -5 }}
                      onMouseEnter={textEnter}
                      onMouseLeave={textLeave}
                    >
                      {/* Glow effect on hover */}
                      <div className="absolute -inset-1 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg blur opacity-0 group-hover:opacity-20 transition-opacity duration-1000" aria-hidden="true"></div>
                      
                      <div className="relative">
                        <div className="mb-4" aria-hidden="true">{item.icon}</div>
                        <h3 className="text-2xl font-bold mb-4 text-white group-hover:text-purple-400 transition-colors">{item.title}</h3>
                        <p className="text-gray-300 text-lg">{item.description}</p>
                      </div>
                    </motion.article>
                  </div>
                  
                  <div className="w-full md:w-1/2">
                    <motion.div 
                      className="rounded-2xl overflow-hidden shadow-2xl shadow-purple-900/20 border border-purple-500/20"
                      whileHover={{ scale: 1.02 }}
                    >
                      <img 
                        src={item.image} 
                        alt={`${item.title} - Professional social media marketing service illustration`}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        decoding="async"
                        width="600"
                        height="400"
                      />
                    </motion.div>
                  </div>
                </motion.div>
              ))}
            </div>
                          
            <motion.div 
              className="mt-16 text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h3 className="text-2xl font-bold mb-6 text-white">Let's Grow Your Social Presence!</h3>
              <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
                Enhance brand trust, increase followers, and drive more conversions with our expert social media marketing solutions that deliver real results.
              </p>
              <motion.button 
                className="px-8 py-4 rounded-full text-base font-medium bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 shadow-lg shadow-purple-600/20 flex items-center justify-center gap-2 mx-auto group"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                onMouseEnter={textEnter}
                onMouseLeave={textLeave}
                aria-label="Elevate your social media presence"
              >
                Elevate Your Social Media
                <FaArrowRight className="transition-transform group-hover:translate-x-1" aria-hidden="true" />
              </motion.button>
            </motion.div>
          </div>
        </section>
        
        {/* Photography Section */}
        <section 
          ref={sectionRefs.photography}
          id="photography" 
          className="py-20 relative overflow-hidden"
          aria-labelledby="photography-heading"
        >
          {/* Background elements */}
          <div className="absolute inset-0 z-0" aria-hidden="true">
            <div className="absolute w-96 h-96 rounded-full bg-purple-900/10 bottom-1/4 -right-48 blur-3xl"></div>
            <div className="absolute w-64 h-64 rounded-full bg-blue-900/10 top-1/3 left-1/4 blur-3xl"></div>
            
            {/* Background image with overlay */}
            <div className="absolute inset-0 opacity-10">
              <img 
                src="https://images.unsplash.com/photo-1542038784456-1ea8e935640e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80" 
                alt="Professional product photography studio setup"
                className="w-full h-full object-cover"
                loading="lazy"
                decoding="async"
                width="2070"
                height="1380"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black via-black/90 to-black"></div>
            </div>
          </div>
          
          <div className="container mx-auto px-4 relative z-10">
            <motion.header 
              className="text-center mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.5 }}
            >
              <h2 id="photography-heading" className="text-3xl md:text-5xl font-bold mb-6">
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-blue-500">
                  Product Photography Services
                </span>
                <span className="block text-white mt-2">High-Quality Visuals for More Sales</span>
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-purple-400 to-blue-500 mx-auto mb-6" aria-hidden="true"></div>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                Professional product photography that showcases your products in the best light, increasing customer trust and driving more sales.
              </p>
            </motion.header>
            
            {/* Featured photography showcase */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
              {photographyImages.map((image, index) => (
                <motion.article
                  key={index}
                  className="rounded-2xl overflow-hidden shadow-lg shadow-purple-900/20 group relative"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  whileHover={{ y: -5 }}
                >
                  <div className="h-64 overflow-hidden">
                    <img 
                      src={image} 
                      alt={`Professional product photography example ${index + 1} showcasing high-quality e-commerce product shots`}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      loading="lazy"
                      decoding="async"
                      width="400"
                      height="300"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end">
                    <div className="p-4">
                      <div className="w-10 h-10 rounded-full bg-purple-600 flex items-center justify-center mb-2" aria-hidden="true">
                        <FaCamera className="text-white text-lg" />
                      </div>
                      <p className="text-white font-medium">Professional Product Shot</p>
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
            
            <div className="flex flex-col lg:flex-row items-center gap-12 mb-16">
              <motion.div 
                className="lg:w-1/2"
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.5 }}
              >
                <h3 className="text-3xl font-bold mb-6 text-white">Showcase Your Products with Excellence</h3>
                <p className="text-gray-300 text-lg mb-8">
                  High-quality product photography is essential for e-commerce success. Our professional photography services help you showcase your products in the best light, increasing customer trust, improving conversion rates, and driving more sales across all platforms.
                </p>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                  {[
                    "Studio & On-Location Shoots",
                    "Multiple Angles & Views", 
                    "Professional Retouching",
                    "E-Commerce Optimized"
                  ].map((feature, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <FaCheckCircle className="text-purple-400 text-xl mt-1 flex-shrink-0" aria-hidden="true" />
                      <span className="text-gray-300">{feature}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
              
              <motion.div 
                className="lg:w-1/2"
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.5 }}
              >
                <div className="relative">
                  <img 
                    src="https://images.unsplash.com/photo-1620674156044-52b714665610?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80" 
                    alt="Professional photography studio with lighting equipment and camera setup for product photography"
                    className="rounded-2xl shadow-2xl shadow-purple-900/20 border border-purple-500/20"
                    loading="lazy"
                    decoding="async"
                    width="2070"
                    height="1380"
                  />
                  <div className="absolute -bottom-6 -left-6 bg-gradient-to-br from-purple-600 to-blue-600 p-4 rounded-2xl shadow-lg transform -rotate-3">
                    <div className="flex items-center gap-3">
                      <FaCamera className="text-white text-xl" aria-hidden="true" />
                      <span className="text-white font-bold">Professional Studio</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
            
            {/* Photography services grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {photographyServices.map((item, index) => (
                <motion.article
                  key={index}
                  className="bg-gradient-to-br from-purple-900/20 to-black border border-purple-900/30 rounded-2xl p-6 hover:border-purple-500/50 transition-all group relative overflow-hidden"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  whileHover={{ y: -5 }}
                  onMouseEnter={textEnter}
                  onMouseLeave={textLeave}
                >
                  {/* Glow effect on hover */}
                  <div className="absolute -inset-1 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg blur opacity-0 group-hover:opacity-20 transition-opacity duration-1000" aria-hidden="true"></div>
                  
                  <div className="relative">
                    <div aria-hidden="true">{item.icon}</div>
                    <h3 className="text-xl font-bold mb-3 text-white group-hover:text-purple-400 transition-colors">{item.title}</h3>
                    <p className="text-gray-300">{item.description}</p>
                  </div>
                </motion.article>
              ))}
            </div>
            
            <motion.div 
              className="mt-16 text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h3 className="text-2xl font-bold mb-6 text-white">Let's Make Your Products Stand Out!</h3>
              <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
                Increase customer trust, boost conversions, and drive more sales with our professional product photography services that showcase your products beautifully.
              </p>
              <motion.button 
                className="px-8 py-4 rounded-full text-base font-medium bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 shadow-lg shadow-purple-600/20 flex items-center justify-center gap-2 mx-auto group"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                onMouseEnter={textEnter}
                onMouseLeave={textLeave}
                aria-label="Start your product photography project"
              >
                Capture Perfect Product Photos
                <FaArrowRight className="transition-transform group-hover:translate-x-1" aria-hidden="true" />
              </motion.button>
            </motion.div>
          </div>
        </section>
        
        {/* Website Maintenance Section */}
        <section 
          ref={sectionRefs.maintenance}
          id="maintenance" 
          className="py-20 relative overflow-hidden"
          aria-labelledby="maintenance-heading"
        >
          {/* Background elements */}
          <div className="absolute inset-0 z-0" aria-hidden="true">
            <div className="absolute w-96 h-96 rounded-full bg-blue-900/10 top-1/4 -right-48 blur-3xl"></div>
            <div className="absolute w-64 h-64 rounded-full bg-purple-900/10 bottom-1/3 left-1/3 blur-3xl"></div>
            
            {/* Background image with overlay */}
            <div className="absolute inset-0 opacity-10">
              <img 
                src="https://images.unsplash.com/photo-1547658719-da2b51169166?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2064&q=80" 
                alt="Website maintenance and optimization dashboard"
                className="w-full h-full object-cover"
                loading="lazy"
                decoding="async"
                width="2064"
                height="1376"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black via-black/90 to-black"></div>
            </div>
            
            {/* Code-like pattern background */}
            <div className="absolute inset-0 overflow-hidden opacity-5">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="absolute text-xs font-mono" style={{ top: `${i * 10}%`, left: `${Math.random() * 100}%` }}>
                  {'{maintenance: true, security: true, performance: true}'}
                </div>
              ))}
            </div>
          </div>

          <div className="container mx-auto px-4 relative z-10">
            <div className="flex flex-col lg:flex-row items-center gap-12 mb-16">
              <motion.div 
                className="lg:w-1/2"
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.5 }}
              >
                <h2 id="maintenance-heading" className="text-3xl md:text-5xl font-bold mb-6">
                  <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-blue-500">
                    Website Maintenance Services
                  </span>
                  <span className="block text-white mt-2">Keep Your Site Secure & Optimized</span>
                </h2>
                <div className="w-24 h-1 bg-gradient-to-r from-purple-400 to-blue-500 mb-6" aria-hidden="true"></div>
                <p className="text-gray-300 text-lg mb-8">
                  Regular website maintenance is crucial for security, performance, and user experience. Our comprehensive maintenance services ensure your website stays updated, secure, and running at peak performance while protecting your investment and maintaining search engine rankings.
                </p>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                  {[
                    "Regular Security Updates",
                    "Performance Optimization",
                    "Content Updates", 
                    "Backup & Recovery"
                  ].map((feature, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <FaCheckCircle className="text-purple-400 text-xl mt-1 flex-shrink-0" aria-hidden="true" />
                      <span className="text-gray-300">{feature}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
              
              <motion.div 
                className="lg:w-1/2"
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.5 }}
              >
                <img 
                  src="https://images.unsplash.com/photo-1461749280684-dccba630e2f6?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2069&q=80" 
                  alt="Website maintenance code editor showing security updates and optimization"
                  className="rounded-2xl shadow-2xl shadow-purple-900/20 border border-purple-500/20"
                  loading="lazy"
                  decoding="async"
                  width="2069"
                  height="1379"
                />
              </motion.div>
            </div>
            
            {/* Maintenance services with icons */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {maintenanceServices.map((item, index) => (
                <motion.article
                  key={index}
                  className="bg-gradient-to-br from-purple-900/20 to-black border border-purple-900/30 rounded-2xl overflow-hidden hover:border-purple-500/50 transition-all group relative"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  whileHover={{ y: -5 }}
                  onMouseEnter={textEnter}
                  onMouseLeave={textLeave}
                >
                  {/* Card image */}
                  <div className="h-48 overflow-hidden">
                    <img 
                      src={item.image} 
                      alt={`${item.title} - Professional website maintenance service illustration`}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      loading="lazy"
                      decoding="async"
                      width="400"
                      height="300"
                    />
                  </div>
                  
                  {/* Card content */}
                  <div className="p-6 relative">
                    {/* Glow effect on hover */}
                    <div className="absolute -inset-1 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg blur opacity-0 group-hover:opacity-20 transition-opacity duration-1000" aria-hidden="true"></div>
                    
                    <div className="relative">
                      <div className="flex items-center mb-4" aria-hidden="true">
                        {item.icon}
                      </div>
                      <h3 className="text-xl font-bold mb-3 text-white group-hover:text-purple-400 transition-colors">{item.title}</h3>
                      <p className="text-gray-300">{item.description}</p>
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
            
            <motion.div 
              className="mt-16 text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h3 className="text-2xl font-bold mb-6 text-white">Let's Keep Your Website Running Smoothly!</h3>
              <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
                Enhance website security, performance, and SEO rankings with our expert maintenance services that protect your investment and ensure optimal performance.
              </p>
              <motion.button 
                className="px-8 py-4 rounded-full text-base font-medium bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 shadow-lg shadow-purple-600/20 flex items-center justify-center gap-2 mx-auto group"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                onMouseEnter={textEnter}
                onMouseLeave={textLeave}
                onClick={() => {
                  window.location.href = "/contact-tes";
                }}
                aria-label="Start maintaining your website with our services"
              >
                Maintain Your Website
                <FaArrowRight className="transition-transform group-hover:translate-x-1" aria-hidden="true" />
              </motion.button>
            </motion.div>
          </div>
        </section>
        
        {/* Why Choose Us Section */}
        <section 
          ref={sectionRefs.why}
          id="why" 
          className="py-20 relative overflow-hidden"
          aria-labelledby="why-heading"
        >
          {/* Background elements */}
          <div className="absolute inset-0 z-0" aria-hidden="true">
            <div className="absolute w-96 h-96 rounded-full bg-purple-900/10 top-1/3 -left-48 blur-3xl"></div>
            <div className="absolute w-64 h-64 rounded-full bg-blue-900/10 bottom-1/4 right-1/4 blur-3xl"></div>
            
            {/* Background image with overlay */}
            <div className="absolute inset-0 opacity-10">
              <img 
                src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80" 
                alt="Professional digital marketing team collaborating on strategies"
                className="w-full h-full object-cover"
                loading="lazy"
                decoding="async"
                width="2070"
                height="1380"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black via-black/90 to-black"></div>
            </div>
          </div>
          
          <div className="container mx-auto px-4 relative z-10">
            <motion.header 
              className="text-center mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.5 }}
            >
              <h2 id="why-heading" className="text-3xl md:text-5xl font-bold mb-6">
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-blue-500">
                  Why Choose Techecosolution
                </span>
                <span className="block text-white mt-2">for Digital Marketing</span>
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-purple-400 to-blue-500 mx-auto mb-6" aria-hidden="true"></div>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                Our expert team delivers data-driven digital marketing solutions that drive real results for your business.
              </p>
            </motion.header>
            
            {/* Team image */}
            <motion.div 
              className="mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-purple-900/20 border border-purple-500/20">
                <img 
                  src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80" 
                  alt="Expert digital marketing team at Techecosolution working on client campaigns"
                  className="w-full h-auto"
                  loading="lazy"
                  decoding="async"
                  width="2070"
                  height="1380"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent opacity-70"></div>
                <div className="absolute bottom-0 left-0 right-0 p-8 text-center">
                  <h3 className="text-3xl font-bold text-white mb-4">Expert Team of Digital Marketers</h3>
                  <p className="text-gray-200 text-lg max-w-3xl mx-auto">
                    Our team of experienced professionals is dedicated to helping your business grow with cutting-edge digital marketing strategies and proven methodologies.
                  </p>
                </div>
              </div>
            </motion.div>
            
            {/* Why choose us cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {whyChooseUs.map((item, index) => (
                <motion.article
                  key={index}
                                    className="group relative rounded-2xl overflow-hidden shadow-lg shadow-purple-900/20 h-[400px]"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  whileHover={{ y: -5 }}
                  onMouseEnter={textEnter}
                  onMouseLeave={textLeave}
                >
                  <img 
                    src={item.image} 
                    alt={`${item.title} - Techecosolution digital marketing advantage and expertise`}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    loading="lazy"
                    decoding="async"
                    width="400"
                    height="300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <div className="flex items-center mb-3" aria-hidden="true">
                      {item.icon}
                    </div>
                    <h3 className="text-xl font-bold mb-2 text-white group-hover:text-purple-400 transition-colors">{item.title}</h3>
                    <p className="text-gray-300">{item.description}</p>
                  </div>
                </motion.article>
              ))}
            </div>

            {/* FAQ Section */}
            <motion.div 
              className="mt-20"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <div className="text-center mb-12">
                <h3 className="text-3xl md:text-4xl font-bold mb-6 text-white">Frequently Asked Questions</h3>
                <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                  Get answers to common questions about our digital marketing services and how we can help grow your business.
                </p>
              </div>
              
              <div className="max-w-4xl mx-auto space-y-6">
                {[
                  {
                    question: "What digital marketing services do you offer?",
                    answer: "We offer comprehensive digital marketing services including SEO optimization, social media marketing, product photography, website maintenance, content creation, and paid advertising campaigns across all major platforms."
                  },
                  {
                    question: "How does SEO optimization help my business?",
                    answer: "SEO optimization improves your website's visibility in search engines, leading to increased organic traffic, better brand awareness, and more qualified leads. Our SEO strategies include keyword research, on-page optimization, technical SEO, and link building."
                  },
                  {
                    question: "Which social media platforms do you manage?",
                    answer: "We manage all major social media platforms including Facebook, Instagram, Twitter, LinkedIn, and YouTube. We create platform-specific content strategies and run targeted advertising campaigns to maximize your reach and engagement."
                  },
                  {
                    question: "What is included in your website maintenance service?",
                    answer: "Our website maintenance includes regular security updates, performance optimization, content updates, backup and recovery, mobile responsiveness testing, and ongoing SEO monitoring to ensure your website runs smoothly and securely."
                  },
                  {
                    question: "How do you measure digital marketing success?",
                    answer: "We measure success through comprehensive analytics including website traffic growth, search engine rankings, social media engagement rates, conversion rates, lead generation, and ROI. We provide detailed monthly reports with actionable insights."
                  }
                ].map((faq, index) => (
                  <motion.details 
                    key={index}
                    className="bg-gradient-to-br from-purple-900/20 to-black border border-purple-900/30 rounded-xl p-6 group"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <summary className="text-xl font-bold cursor-pointer hover:text-purple-400 transition-colors list-none">
                      <div className="flex items-center justify-between">
                        <span>{faq.question}</span>
                        <motion.div
                          className="ml-4"
                          whileHover={{ rotate: 180 }}
                          transition={{ duration: 0.3 }}
                          aria-hidden="true"
                        >
                          <FaArrowRight className="text-purple-500" />
                        </motion.div>
                      </div>
                    </summary>
                    <p className="mt-4 text-gray-300 leading-relaxed">
                      {faq.answer}
                    </p>
                  </motion.details>
                ))}
              </div>
            </motion.div>

            {/* CTA Section */}
            <motion.div 
              className="mt-20 text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <div className="relative">
                {/* Neon border glow */}
                <div className="absolute -inset-1 bg-gradient-to-r from-purple-600 to-blue-600 rounded-3xl blur-xl opacity-70" aria-hidden="true"></div>
                
                <div className="relative bg-black border border-purple-500/50 rounded-3xl p-12">
                  <h3 className="text-3xl md:text-4xl font-bold mb-6 text-white">Ready to Transform Your Digital Presence?</h3>
                  <p className="text-xl text-gray-300 mb-10 max-w-3xl mx-auto">
                    Partner with Techecosolution for comprehensive digital marketing services that drive real results. From SEO to social media, we've got your growth covered.
                  </p>
                  
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <motion.button 
                      className="px-10 py-4 rounded-full text-lg font-medium bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 shadow-lg shadow-purple-600/20 flex items-center justify-center gap-2 group"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.98 }}
                      onMouseEnter={textEnter}
                      onMouseLeave={textLeave}
                      onClick={() => {
                        window.location.href = "/contact-tes";
                      }}
                      aria-label="Get started with digital marketing services"
                    >
                      Get Started Today
                      <FaArrowRight className="transition-transform group-hover:translate-x-1" aria-hidden="true" />
                    </motion.button>
                    
                    <motion.button 
                      className="px-10 py-4 rounded-full text-lg font-medium border border-purple-500/50 hover:bg-purple-900/20 transition-all flex items-center justify-center gap-2"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.98 }}
                      onMouseEnter={textEnter}
                      onMouseLeave={textLeave}
                      // onClick={() => {
                      //   window.location.href = "/portfolio";
                      // }}
                      aria-label="View our digital marketing portfolio"
                    >
                      View Our Portfolio
                    </motion.button>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Custom animations */}
        <style jsx>{`
          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-20px); }
          }
          
          @keyframes float-slow {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-10px); }
          }
          
          @keyframes pulse-slow {
            0%, 100% { opacity: 0.1; }
            50% { opacity: 0.3; }
          }
          
          .animate-float {
            animation: float 6s ease-in-out infinite;
          }
          
          .animate-float-slow {
            animation: float-slow 8s ease-in-out infinite;
          }
          
          .animate-pulse-slow {
            animation: pulse-slow 4s ease-in-out infinite;
          }
        `}</style>
      </main>
    </>
  );
};

export default DigitalMarketingServices;

